/**
 * "From your past papers" (bug fix 1, spec 9 §5): what the writer actually uses — the shapes
 * of the questions — summarised, then the questions grouped by paper. Pure; computed from the
 * exemplars the page already has.
 */
import type { Exemplar } from "../api/api";
import { commandOf } from "./commands";

export const patterns = (es: Exemplar[]) => es.filter((e) => e.pattern !== false);

/** Questions at each mark value, ascending. */
export function histogram(es: Exemplar[]): { marks: number; count: number }[] {
  const by = new Map<number, number>();
  for (const e of patterns(es)) by.set(e.marks, (by.get(e.marks) ?? 0) + 1);
  return [...by.entries()].sort((a, b) => a[0] - b[0]).map(([marks, count]) => ({ marks, count }));
}

/** The narrowest run of mark values holding at least half the questions: "1–3". */
export function modalBand(hist: { marks: number; count: number }[]): [number, number] | null {
  const total = hist.reduce((n, h) => n + h.count, 0);
  if (total === 0) return null;
  let best: [number, number] | null = null;
  for (let i = 0; i < hist.length; i++) {
    let sum = 0;
    for (let j = i; j < hist.length; j++) {
      sum += hist[j]!.count;
      if (sum * 2 >= total) {
        const band: [number, number] = [hist[i]!.marks, hist[j]!.marks];
        if (!best || band[1] - band[0] < best[1] - best[0]) best = band;
        break;
      }
    }
  }
  return best;
}

/** "Most questions are worth 1–3 marks; 6-mark questions are usually Explain or Describe". */
export function caption(es: Exemplar[], words: string[]): string {
  const hist = histogram(es);
  const band = modalBand(hist);
  if (!band) return "";
  const range = band[0] === band[1] ? `${band[0]}` : `${band[0]}–${band[1]}`;
  const head = `Most questions are worth ${range} mark${band[1] === 1 ? "" : "s"}`;
  // The top of the range: the highest mark values that together hold a tenth of the questions,
  // so one 11-mark question doesn't speak for the paper's long answers.
  const total = hist.reduce((n, h) => n + h.count, 0);
  let from = hist.length - 1;
  let held = hist[from]!.count;
  while (held * 10 < total && from > 0 && hist[from - 1]!.marks > band[1])
    held += hist[--from]!.count;
  const floor = hist[from]!.marks;
  if (floor <= band[1]) return head;
  const at = patterns(es).filter((e) => e.marks >= floor);
  const counts = new Map<string, number>();
  for (const e of at) {
    const c = commandOf(e.text, words);
    if (c) counts.set(c, (counts.get(c) ?? 0) + 1);
  }
  const usual = [...counts.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, 2)
    .map(([w]) => w);
  if (usual.length === 0) return head;
  const which =
    from === hist.length - 1 ? `${floor}-mark questions` : `questions worth ${floor} marks or more`;
  return `${head}; ${which} are usually ${usual.join(" or ")}`;
}

export type Group = { key: string; exemplars: Exemplar[]; marks: number };

/** One group per past paper (its set when there is one, else its file), page order inside. */
export function byPaper(es: Exemplar[]): Group[] {
  const groups = new Map<string, Exemplar[]>();
  for (const e of es) {
    const key = e.past_paper_id ?? e.document_id;
    groups.set(key, [...(groups.get(key) ?? []), e]);
  }
  return [...groups.entries()].map(([key, list]) => {
    const sorted = [...list].sort((a, b) => a.page - b.page);
    return { key, exemplars: sorted, marks: sorted.reduce((n, e) => n + e.marks, 0) };
  });
}

/** "In a Standard paper (40 marks): about 8 marks of multiple choice, 18 of short answer". */
export function splitFor(
  types: { type: string; marks: number }[],
  paper = 40,
): { type: string; marks: number }[] {
  const all = types.reduce((n, t) => n + t.marks, 0);
  if (all <= 0) return [];
  return types.map((t) => ({ type: t.type, marks: Math.round((t.marks / all) * paper) }));
}
